/**
 * Staff editor for the quarterly all-hands update. Pick a quarter,
 * the KPI board auto-fills from the pipeline + WiP ledger (any
 * headline figure pencil-overridable), complete the editorial
 * sections, save the draft, then Publish to freeze a snapshot and
 * mint the @medicysltd.co.uk-gated share link.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Eye, Loader2, Save, Send, Sparkles } from "lucide-react";
import StaffShell from "../components/staff/StaffShell";
import QuarterKpis from "../components/quarter/QuarterKpis";
import RichTextEditor, { looksLikeHtml, mdliteToHtml, POSITIVE_EMOJIS, NEGATIVE_EMOJIS } from "../components/quarter/RichTextEditor";
import { useAuth, renderApiError } from "../lib/auth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

// Legacy drafts stored these sections as bullet arrays; render them
// into rich-editor HTML. Plain text falls through mdliteToHtml.
const toRichHtml = (v) => {
  if (Array.isArray(v)) return v.length ? mdliteToHtml(v.map((s) => `- ${s}`).join("\n")) : "";
  const s = v || "";
  return looksLikeHtml(s) ? s : mdliteToHtml(s);
};

function SectionEditor({ label, hint, value, onChange, testid, emojis }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4">
      <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">{label}</p>
      {hint && <p className="text-[10px] text-stone-400 mt-0.5">{hint}</p>}
      <RichTextEditor
        value={value}
        onChange={onChange}
        placeholder="Write here…"
        testid={testid}
        idPrefix={`${testid}-rte`}
        minHeight="9rem"
        emojis={emojis}
      />
    </div>
  );
}

function AudiencePanel({ year, q, publishedAt }) {
  const [aud, setAud] = useState(null);
  useEffect(() => {
    if (!year || !q) return undefined;
    let dead = false;
    axios
      .get(`${API}/api/quarter-glance/${year}/${q}/audience`)
      .then(({ data }) => {
        if (!dead) setAud(data);
      })
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, [year, q, publishedAt]);
  if (!aud?.published) return null;
  const fmt = (iso) =>
    iso
      ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
      : "";
  return (
    <div className="rounded-xl border border-stone-200 bg-white px-4 py-3" data-testid="quarter-audience-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Eye className="w-3.5 h-3.5 text-stone-400" />
        <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">Who opened it</p>
        {aud.viewers.length > 0 && (
          <span className="text-[10px] text-stone-400" data-testid="quarter-audience-totals">
            {aud.unique_viewers} {aud.unique_viewers === 1 ? "person" : "people"} · {aud.total_views}{" "}
            {aud.total_views === 1 ? "open" : "opens"}
          </span>
        )}
      </div>
      {aud.viewers.length === 0 ? (
        <p className="text-xs text-stone-400 mt-2" data-testid="quarter-audience-empty">
          Nobody has opened the link yet. Every open is logged here with the email entered at the gate.
        </p>
      ) : (
        <div className="mt-2 divide-y divide-stone-100">
          {aud.viewers.map((v) => (
            <div
              key={v.email}
              className="flex items-center justify-between gap-3 py-1.5 flex-wrap"
              data-testid={`quarter-audience-row-${v.email}`}
            >
              <span className="text-xs font-mono text-slate-800">
                {v.email}
                {v.staff && (
                  <span className="ml-2 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-[#1B4F8A]/10 text-[#1B4F8A]">
                    staff
                  </span>
                )}
              </span>
              <span className="text-[11px] text-stone-500">
                {v.views} {v.views === 1 ? "open" : "opens"} · first {fmt(v.first_seen)} · last {fmt(v.last_seen)}
              </span>
            </div>
          ))}
        </div>
      )}
      {aud.denied?.length > 0 && (
        <p className="text-[11px] text-rose-600 mt-2" data-testid="quarter-audience-denied">
          Blocked at the gate:{" "}
          {aud.denied
            .slice(0, 6)
            .map((d) => d.email || "invalid email")
            .join(", ")}
        </p>
      )}
    </div>
  );
}

export default function QuarterGlance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [quarters, setQuarters] = useState([]);
  const [selected, setSelected] = useState(null); // {year,q}
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // form state
  const [aiSummary, setAiSummary] = useState("");
  const [generating, setGenerating] = useState(false);
  const [highlights, setHighlights] = useState("");
  const [lowlights, setLowlights] = useState("");
  const [lookingAhead, setLookingAhead] = useState("");
  const [prioTitle, setPrioTitle] = useState("");
  const [prioText, setPrioText] = useState("");
  const [generatingPriority, setGeneratingPriority] = useState(false);
  const [aiDialog, setAiDialog] = useState(null); // "summary" | "priority" | null
  const [guidance, setGuidance] = useState("");
  const [signature, setSignature] = useState("Frank");
  const [overrides, setOverrides] = useState({});
  const [takeaways, setTakeaways] = useState({});
  const [takeawaysAt, setTakeawaysAt] = useState(null);
  const [twBusy, setTwBusy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/quarter-glance/quarters`).then(({ data: d }) => {
      setQuarters(d.quarters || []);
      if (d.quarters?.length) {
        // Default to the most recent COMPLETED quarter (the one you
        // report on), i.e. the second entry.
        const pick = d.quarters[1] || d.quarters[0];
        setSelected({ year: pick.year, q: pick.q });
      }
    }).catch(() => setLoading(false));
  }, []);

  const load = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/api/quarter-glance/${selected.year}/${selected.q}`,
      );
      setData(d);
      const e = d.editorial || {};
      const rawSummary = e.ai_summary || "";
      setAiSummary(looksLikeHtml(rawSummary) ? rawSummary : mdliteToHtml(rawSummary));
      setHighlights(toRichHtml(e.highlights));
      setLowlights(toRichHtml(e.lowlights));
      setLookingAhead(toRichHtml(e.looking_ahead));
      setPrioTitle(e.one_priority_title || "");
      setPrioText(e.one_priority || "");
      setSignature(e.signature || "Frank");
      setOverrides(d.kpi_overrides || {});
      setTakeaways(d.takeaways || {});
      setTakeawaysAt(d.takeaways_at || null);
    } catch (err) {
      toast.error(renderApiError(err, "Couldn't load the quarter."));
    } finally {
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => {
    load();
  }, [load]);

  const body = () => ({
    ai_summary: aiSummary,
    highlights,
    lowlights,
    looking_ahead: lookingAhead,
    one_priority_title: prioTitle,
    one_priority: prioText,
    signature,
    kpi_overrides: overrides,
    takeaways,
  });

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/quarter-glance/${selected.year}/${selected.q}`, body());
      toast.success("Draft saved.");
      await load();
    } catch (err) {
      toast.error(renderApiError(err, "Couldn't save."));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!selected) return;
    setPublishing(true);
    try {
      await axios.put(`${API}/api/quarter-glance/${selected.year}/${selected.q}`, body());
      const { data: d } = await axios.post(
        `${API}/api/quarter-glance/${selected.year}/${selected.q}/publish`,
      );
      toast.success("Published - share link ready.");
      if (d.share_url && navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(d.share_url);
          toast.info("Link copied to clipboard.");
        } catch {
          /* clipboard denied */
        }
      }
      await load();
    } catch (err) {
      toast.error(renderApiError(err, "Couldn't publish."));
    } finally {
      setPublishing(false);
    }
  };

  const generateSummary = async (guide = "") => {
    if (!selected) return;
    setGenerating(true);
    try {
      const { data: d } = await axios.post(
        `${API}/api/quarter-glance/${selected.year}/${selected.q}/generate-summary`,
        {
          highlights,
          lowlights,
          looking_ahead: lookingAhead,
          one_priority_title: prioTitle,
          one_priority: prioText,
          guidance: guide,
        },
      );
      setAiSummary(mdliteToHtml(d.summary || ""));
      toast.success("Draft written - tweak the words, then save.");
    } catch (err) {
      toast.error(renderApiError(err, "Couldn't generate the summary."));
    } finally {
      setGenerating(false);
    }
  };

  const generatePriority = async (guide = "") => {
    if (!selected) return;
    setGeneratingPriority(true);
    try {
      const { data: d } = await axios.post(
        `${API}/api/quarter-glance/${selected.year}/${selected.q}/generate-priority`,
        { highlights, lowlights, looking_ahead: lookingAhead, guidance: guide },
      );
      setPrioTitle(d.title || "");
      setPrioText(d.body || "");
      toast.success("Priority suggested - tweak it, then save.");
    } catch (err) {
      toast.error(renderApiError(err, "Couldn't suggest the priority."));
    } finally {
      setGeneratingPriority(false);
    }
  };

  const targetHasText = (target) =>
    target === "priority"
      ? Boolean(prioTitle.trim() || prioText.trim())
      : Boolean(aiSummary.replace(/<[^>]*>/g, "").trim());

  const openAiDialog = (target) => {
    setGuidance("");
    setAiDialog(target);
  };

  const runAiDialog = () => {
    const target = aiDialog;
    const guide = guidance.trim();
    setAiDialog(null);
    if (target === "priority") generatePriority(guide);
    else generateSummary(guide);
  };

  const saveTakeaway = (key, lines) => {
    setTakeaways((prev) => ({ ...prev, [key]: lines }));
    toast.message("Takeaways updated (save to keep).");
  };

  const regenTakeaways = async (key) => {
    if (!selected) return;
    setTwBusy(key || "all");
    const startedAt = new Date().toISOString();
    try {
      const { data: d } = await axios.post(
        `${API}/api/quarter-glance/${selected.year}/${selected.q}/generate-takeaways`,
        { key: key || null },
      );
      setTakeaways((prev) => ({
        ...prev,
        ...(key ? { [key]: (d.takeaways || {})[key] || [] } : d.takeaways || {}),
      }));
      setTakeawaysAt(d.takeaways_at || null);
      toast.success(key ? "Takeaways rewritten - tweak them, then save." : "Key takeaways written for every card - tweak them, then save.");
    } catch (err) {
      // The gateway can cut long generations (~100s) even though the
      // server finishes and saves - poll for the result before failing.
      const recovered = await (async () => {
        for (let i = 0; i < 8; i += 1) {
          await new Promise((r) => setTimeout(r, 12000));
          try {
            const { data: d } = await axios.get(
              `${API}/api/quarter-glance/${selected.year}/${selected.q}`,
            );
            if (d.takeaways_at && d.takeaways_at > startedAt) {
              setTakeaways((prev) => ({
                ...prev,
                ...(key ? { [key]: (d.takeaways || {})[key] || [] } : d.takeaways || {}),
              }));
              setTakeawaysAt(d.takeaways_at);
              return true;
            }
          } catch {
            /* keep polling */
          }
        }
        return false;
      })();
      if (recovered) {
        toast.success("Key takeaways written (the connection dropped but the writing finished).");
      } else {
        toast.error(renderApiError(err, "Couldn't write the takeaways."));
      }
    } finally {
      setTwBusy(null);
    }
  };

  const onOverride = (key, value) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (value === null || value === "") delete next[key];
      else next[key] = value;
      return next;
    });
    // Reflect immediately in the preview.
    setData((prev) => {
      if (!prev) return prev;
      const kpis = { ...prev.kpis };
      if (key.startsWith("recommend_")) {
        kpis.recommend = { ...kpis.recommend, [key.split("_")[1]]: Number(value) };
      } else if (kpis[key]) {
        kpis[key] = value === null || value === ""
          ? { ...kpis[key], overridden: false }
          : { ...kpis[key], value: Number(value), overridden: true };
      }
      return { ...prev, kpis };
    });
    toast.message(value == null || value === "" ? "Figure back to auto (save to keep)." : "Figure overridden (save to keep).");
  };

  return (
    <StaffShell>
      <div className="space-y-6" data-testid="quarter-glance-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 leading-tight">
              Quarter at a Glance
            </h1>
            <p className="text-sm text-stone-500 mt-1 max-w-2xl">
              The quarterly all-hands update. KPIs fill themselves from the pipeline
              and the WiP ledger - complete the words, then publish a gated link for the team.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selected ? `${selected.year}-${selected.q}` : ""}
              onChange={(e) => {
                const [y, q] = e.target.value.split("-");
                setSelected({ year: Number(y), q: Number(q) });
              }}
              data-testid="quarter-select"
              className="border border-stone-300 rounded-xl px-3 py-2 text-sm bg-white"
            >
              {quarters.map((qq) => (
                <option key={qq.id} value={`${qq.year}-${qq.q}`}>
                  {qq.label}
                  {qq.status === "published" ? " · published" : qq.status === "draft" ? " · draft" : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => regenTakeaways(null)}
              disabled={twBusy !== null || !isAdmin || loading}
              data-testid="quarter-takeaways-all-btn"
              title={isAdmin ? "AI writes the Key takeaways under every KPI card" : "Admin only"}
              className="inline-flex items-center gap-1.5 rounded-xl border border-[#1f7468]/40 bg-[#1f7468]/5 px-3 py-2 text-sm text-[#1f7468] hover:bg-[#1f7468]/10 transition-colors disabled:opacity-50"
            >
              {twBusy === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              AI takeaways
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !isAdmin}
              data-testid="quarter-save-btn"
              title={isAdmin ? "" : "Admin only"}
              className="inline-flex items-center gap-1.5 rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-slate-700 hover:border-[#1B4F8A] transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save draft
            </button>
            <button
              type="button"
              onClick={publish}
              disabled={publishing || !isAdmin}
              data-testid="quarter-publish-btn"
              title={isAdmin ? "" : "Admin only"}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#1B4F8A] px-3 py-2 text-sm text-white hover:bg-[#163f6e] transition-colors disabled:opacity-50"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {data?.status === "published" ? "Re-publish" : "Publish"}
            </button>
          </div>
        </div>

        {data?.share_url && (
          <div
            className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 flex items-center gap-3 flex-wrap"
            data-testid="quarter-share-url"
          >
            <span className="text-xs text-teal-800">
              Published{data.published_at ? ` ${new Date(data.published_at).toLocaleString("en-GB")}` : ""} — paste this gated link into your email to the team:
            </span>
            <code className="text-xs font-mono text-teal-900 break-all">{data.share_url}</code>
            <button
              type="button"
              data-testid="quarter-copy-link-btn"
              onClick={() => {
                navigator.clipboard?.writeText(data.share_url);
                toast.success("Link copied.");
              }}
              className="inline-flex items-center gap-1 text-xs text-teal-800 hover:text-teal-950"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        )}

        {data?.share_url && (
          <AudiencePanel year={selected?.year} q={selected?.q} publishedAt={data?.published_at} />
        )}

        {loading || !data ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm py-16 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Crunching the quarter…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionEditor
                label="Highlights"
                hint="Bullets, bold and highlights via the toolbar."
                value={highlights}
                onChange={setHighlights}
                testid="quarter-highlights-input"
                emojis={POSITIVE_EMOJIS}
              />
              <SectionEditor
                label="Lowlights"
                hint="Bullets, bold and highlights via the toolbar."
                value={lowlights}
                onChange={setLowlights}
                testid="quarter-lowlights-input"
                emojis={NEGATIVE_EMOJIS}
              />
              <SectionEditor
                label="Looking ahead"
                hint="Bullets, bold and highlights via the toolbar."
                value={lookingAhead}
                onChange={setLookingAhead}
                testid="quarter-ahead-input"
              />
            </div>

            <QuarterKpis
              kpis={data.kpis}
              editable={isAdmin}
              onOverride={onOverride}
              takeaways={takeaways}
              takeawaysAt={takeawaysAt}
              onSaveTakeaway={saveTakeaway}
              onRegenTakeaway={regenTakeaways}
              regenerating={twBusy}
            />

            <div className="rounded-2xl border border-stone-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">
                    The ONE priority
                  </p>
                  <p className="text-[10px] text-stone-400 mt-0.5 max-w-md">
                    Tell the AI your priority topic and it writes the headline and rationale,
                    backed by the quarter&apos;s figures - edit it to make it yours.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openAiDialog("priority")}
                  disabled={generatingPriority || !isAdmin}
                  data-testid="quarter-priority-generate-btn"
                  title={isAdmin ? "" : "Admin only"}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-[#1f7468]/40 bg-[#1f7468]/5 px-3 py-2 text-sm text-[#1f7468] hover:bg-[#1f7468]/10 transition-colors disabled:opacity-50"
                >
                  {generatingPriority ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {generatingPriority ? "Writing the priority…" : (prioTitle.trim() || prioText.trim()) ? "Rewrite with AI" : "Write with AI"}
                </button>
              </div>
              <input
                value={prioTitle}
                onChange={(e) => setPrioTitle(e.target.value)}
                placeholder="Priority headline (e.g. SMART WORKING)"
                data-testid="quarter-priority-title-input"
                className="w-full mt-2 border border-stone-200 rounded-xl px-3 py-2 text-sm font-medium"
              />
              <textarea
                value={prioText}
                onChange={(e) => setPrioText(e.target.value)}
                rows={6}
                data-testid="quarter-priority-input"
                className="w-full mt-2 border border-stone-200 rounded-xl px-3 py-2 text-sm resize-y"
              />
            </div>

            <div
              className="rounded-2xl border border-stone-200 bg-white p-4"
              data-testid="quarter-ai-summary-card"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">
                    The quarter in words
                  </p>
                  <p className="text-[10px] text-stone-400 mt-0.5 max-w-md">
                    AI summarises every section above - the figures, your Highlights, Lowlights,
                    Looking ahead and ONE priority - and lays out a plan of action. Edit and
                    format freely before publishing.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openAiDialog("summary")}
                  disabled={generating || !isAdmin}
                  data-testid="quarter-ai-generate-btn"
                  title={isAdmin ? "" : "Admin only"}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-[#1f7468]/40 bg-[#1f7468]/5 px-3 py-2 text-sm text-[#1f7468] hover:bg-[#1f7468]/10 transition-colors disabled:opacity-50"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {generating ? "Writing the quarter…" : aiSummary.replace(/<[^>]*>/g, "").trim() ? "Regenerate with AI" : "Generate with AI"}
                </button>
              </div>
              <RichTextEditor
                value={aiSummary}
                onChange={setAiSummary}
                placeholder="Complete the sections above, then click “Generate with AI” - or write your own."
                testid="quarter-ai-summary-input"
              />
              <input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                data-testid="quarter-signature-input"
                className="mt-2 border border-stone-200 rounded-xl px-3 py-2 text-sm w-48"
                placeholder="Signed by"
              />
            </div>
          </>
        )}

        <Dialog open={!!aiDialog} onOpenChange={(o) => !o && setAiDialog(null)}>
          <DialogContent data-testid="ai-guidance-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif font-normal text-xl">
                {aiDialog === "priority" ? "Suggest the ONE priority" : "Write the quarter in words"}
              </DialogTitle>
              <DialogDescription>
                {aiDialog === "priority"
                  ? "Tell the AI what the priority should be about - it will write the headline and rationale around your topic, backed by the quarter's figures, highlights, lowlights and looking ahead. It will not pick its own topic."
                  : "Optionally tell the AI what to cover - it will address your points and still add anything else the numbers and sections justify."}
              </DialogDescription>
            </DialogHeader>
            <textarea
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              rows={4}
              autoFocus
              placeholder={
                aiDialog === "priority"
                  ? "e.g. Getting projects into field faster after SOW signature"
                  : "e.g. Mention the OTIS launch, thank the ops team, focus the plan on converting the pipeline…"
              }
              data-testid="ai-guidance-input"
              className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1f7468]/30 resize-y"
            />
            {aiDialog && targetHasText(aiDialog) && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="ai-guidance-overwrite-warning">
                This will replace the current text in the section.
              </p>
            )}
            <DialogFooter>
              <button
                type="button"
                onClick={() => setAiDialog(null)}
                data-testid="ai-guidance-cancel-btn"
                className="inline-flex items-center rounded-xl border border-stone-300 bg-white px-4 py-2 text-sm text-slate-700 hover:border-stone-400 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runAiDialog}
                disabled={aiDialog === "priority" && !guidance.trim()}
                data-testid="ai-guidance-generate-btn"
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#1f7468] px-4 py-2 text-sm text-white hover:bg-[#185d53] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-4 h-4" />
                Generate
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </StaffShell>
  );
}

